export type SearchPostData = {
    query: string;
    lang: string;
    mode: string;
    prompt_version?: string;
    image_data?: string;
    image_type?: string;
}

export type SearchResponseEventType =
    | "text"
    | "page"
    | "scenario"
    | "keywords"
    | "question"
    | "intent_suggestions"
    | "related_questions"
    | "close"
    | "system_text"
    | "history_id"
    | "deep_research"
    | "deep_research_followup"
    | "deep_research_reset";

export type RelatedQuestionsPayload = {
    suggestions: string[];
    lang: string;
};
//#endregion

export type SearchOptions = {
    keepDeepResearchPanel?: boolean;
    skipUserMessage?: boolean;
    isFollowUp?: boolean;
    followUpTaskId?: string;
    isRetry?: boolean;
};

export type FileData = {
    base64: string;
    fileType: string;
    fileUrl: string;
};

export type SearchPayload = {
    question: string;
    fileData: FileData | null;
    options: SearchOptions;
};

export type SearchResult = {
    question: string;
    answerMarkdown: string;
    answerHtml: string;
    answerPlain: string;
    followUpTaskId?: string;
    historyId?: string;
};

export type MessageType = "text" | "typing" | "page" | "scenarios" | "system_text" | "image" | "intent_suggestions" | "session_divider";
export type MessageAuthor = "me" | "support";

export type MessageDataPage = {
    no: string;
    id: number;
    url: string;
    title: string;
    translate_title: string;
};

export type MessageDataScenarios = {
    text: string;
    translated_text: string;
};

export type IntentSuggestion = {
    question: string;
    keywords: string[];
};

export type IntentSuggestionsData = {
    suggestions: IntentSuggestion[];
    reason: string;
};

export type FollowupQuestion = string | { label: string; value: string; isRetry?: boolean };

export type Message = {
    author: MessageAuthor;
    type: MessageType;
    text: string;
    pages: MessageDataPage[];
    historyId: string;
    sessionId?: string;
    inquiryId?: string;
    answer: boolean;
    scenarios: MessageDataScenarios[];
    isGoodReviewed: boolean;
    isBadReviewed: boolean;
    copyText: string;
    language: string;
    reviewPattern: number;
    keywordStr: string;
    keywordUrl: string;
    questions: FollowupQuestion[];
    relatedQuestions?: string[];
    relatedQuestionsLang?: string;
    imageUrl: string;
    intentSuggestions?: IntentSuggestionsData;
    isFollowUp?: boolean;
    followUpTaskId?: string;
    intentReason?: string;
};

export type ReviewData = {
    historyId: string;
    evaluation: number;
    comment: string;
};

export enum Evaluate {
    good = 1,
    bad = -1,
}

export enum ChatMode {
    general = "general",
    gomi = "gomi",
}

export type SpecialMode = {
    code: string;
    name: string;
    description?: string;
    sort_order?: number;
};

export type FollowUpStatus = "pending" | "running" | "success" | "error";

export type FollowUpTask = {
    id: string;
    question: string;
    status: FollowUpStatus;
    responseHtml: string;
    responseMarkdown: string;
    responsePlain: string;
    historyId: string;
    createdAt: string;
    completedAt?: string;
    errorMessage?: string;
    followUpTaskId?: string;
};

export type DeepResearchStep = {
    id: string;
    title: string;
    status?: "pending" | "in_progress" | "completed" | string;
    detail?: string;
    timestamp?: string;
    content?: string;
    completed?: boolean;
    startedAt?: string | null;
    completedAt?: string | null;
    metadata?: Record<string, unknown>;
};

export type DeepResearchReference = {
    id: string;
    title: string;
    url?: string;
    snippet?: string;
    metadata?: Record<string, unknown>;
};

export type DeepResearchMetadata = Record<string, string>;

export type DeepResearchPayload = {
    active?: boolean;
    keywords?: string[];
    progressLog?: string[];
    steps?: DeepResearchStep[];
    references?: DeepResearchReference[];
    summary?: string;
    metadata?: DeepResearchMetadata | Record<string, unknown>;
    followUpQuestions?: string[];
};

export type DeepResearchStatus =
    | "idle"
    | "queued"
    | "running"
    | "synthesizing"
    | "completed"
    | "failed";

export type DeepResearchEventType =
    | "status"
    | "step"
    | "progress"
    | "insight"
    | "summary"
    | "follow_up"
    | "reference"
    | "metadata"
    | "error"
    | "debug";

export type DeepResearchTimelineEntry = {
    type: DeepResearchEventType | string;
    text: string;
    timestamp: string;
    metadata?: Record<string, unknown>;
};

export type DeepResearchState = {
    status: DeepResearchStatus;
    currentStep: number | null;
    totalSteps: number | null;
    steps: DeepResearchStep[];
    summary: string | null;
    insights: string[];
    references: DeepResearchReference[];
    followUpQuestions: string[];
    timeline: DeepResearchTimelineEntry[];
    error: string | null;
    sessionId?: string;
    historyId?: string;
    inquiryId?: string;
    lastUpdatedAt?: string | null;
};

export type ClientInquiryResponse = {
    sessionId: string;
    historyId: string;
    inquiryId: string;
    inquiry: string;
    answer?: string;
    createdAt: string;
    metadata?: Record<string, unknown>;
};
